export const PROPOSAL_SERVICE_TYPES = [
  'Commercial Cleaning',
  'Office Cleaning',
  'Deep Cleaning',
  'Carpet Cleaning',
  'Window Cleaning',
  'Event Cleaning',
];

export const PROPOSAL_FREQUENCY_OPTIONS = [
  'Weekly',
  'Fortnightly',
  'Monthly',
  'One-time',
  'Daily',
];

export function toDateInputValue(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PROPOSAL_TERM_HEADINGS = [
  ['Service Terms', 'serviceTerms'],
  ['Client Responsibilities', 'responsibilities'],
  ['Service Exclusions', 'exclusions'],
  ['Scope-Specific Notes', 'notes'],
];

export function formatProposalTerms(rows) {
  return PROPOSAL_TERM_HEADINGS.map(([title, section]) => {
    const lines = (Array.isArray(rows) ? rows : [])
      .filter((row) => row?.section === section && row.selected !== false)
      .map((row) => {
        if (section === 'serviceTerms') {
          return [row.title, row.body]
            .map((part) => String(part || '').trim())
            .filter(Boolean)
            .join(': ');
        }
        return String(row.text || '').trim();
      })
      .filter(Boolean);
    return lines.length ? `${title}\n${lines.join('\n')}` : '';
  })
    .filter(Boolean)
    .join('\n\n');
}

/** Quotation-style term sections for the proposal view and the client copy. */
export function proposalTermSections(text) {
  const raw = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!raw) return [];
  const headingKind = new Map(
    PROPOSAL_TERM_HEADINGS.map(([title, section]) => [
      title,
      section === 'serviceTerms' ? 'pairs' : 'lines',
    ]),
  );
  const lines = raw.split('\n');
  if (!lines.some((line) => headingKind.has(line.trim()))) {
    const pairs = [];
    const notes = [];
    raw.split(/\n+/).map((line) => line.replace(/^[-•]\s*/, '').trim()).filter(Boolean).forEach((line) => {
      const splitAt = line.indexOf(': ');
      if (splitAt > 0 && splitAt < 80) {
        pairs.push({ title: line.slice(0, splitAt).trim(), body: line.slice(splitAt + 2).trim() });
      } else {
        notes.push(line);
      }
    });
    return [
      pairs.length ? { title: 'Service Terms', kind: 'pairs', rows: pairs } : null,
      notes.length ? { title: 'Terms', kind: 'lines', rows: notes } : null,
    ].filter(Boolean);
  }
  const sections = [];
  let current = null;
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (headingKind.has(trimmed)) {
      if (current?.rows.length) sections.push(current);
      current = { title: trimmed, kind: headingKind.get(trimmed), rows: [] };
      return;
    }
    if (!current || !trimmed) return;
    if (current.kind === 'pairs') {
      const splitAt = trimmed.indexOf(': ');
      current.rows.push(
        splitAt > 0
          ? { title: trimmed.slice(0, splitAt).trim(), body: trimmed.slice(splitAt + 2).trim() }
          : { title: '', body: trimmed },
      );
    } else {
      current.rows.push(trimmed.replace(/^[-•]\s*/, ''));
    }
  });
  if (current?.rows.length) sections.push(current);
  return sections;
}

export function rowsFromProposalTerms(text) {
  const sections = proposalTermSections(text);
  const headed = sections.some((section) =>
    PROPOSAL_TERM_HEADINGS.some(([title]) => title === section.title),
  );
  if (!headed) return null;
  const kind = new Map(PROPOSAL_TERM_HEADINGS);
  return sections.flatMap((section) => {
    const sectionKey = kind.get(section.title);
    if (!sectionKey) return [];
    if (section.kind === 'pairs') {
      return section.rows.map((row, index) => ({
        key: `saved-${sectionKey}-${index}`,
        section: sectionKey,
        title: row.title || '',
        body: row.body || '',
        text: '',
        selected: true,
        custom: true,
        sources: [],
      }));
    }
    return section.rows.map((line, index) => ({
      key: `saved-${sectionKey}-${index}`,
      section: sectionKey,
      title: '',
      body: '',
      text: line,
      selected: true,
      custom: true,
      sources: [],
    }));
  });
}

export function computeProposalTotal({ serviceCost, gstAmount, additionalCharges, discount }) {
  const total =
    (Number(serviceCost) || 0) +
    (Number(gstAmount) || 0) +
    (Number(additionalCharges) || 0) -
    (Number(discount) || 0);
  return Math.max(0, total);
}

export const PROPOSAL_EXPIRING_SOON_DAYS = 7;
export const PROPOSAL_FOLLOW_UP_DAYS = 5;

export const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SENT', label: 'Sent' },
  { key: 'VIEWED', label: 'Viewed' },
  { key: 'REVISION', label: 'Revision' },
  { key: 'SIGNED', label: 'Signed' },
  { key: 'DECLINED', label: 'Declined' },
  { key: 'EXPIRED', label: 'Expired' },
];

export function formatProposalDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function isExpiringSoon(expiryDate) {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  const now = new Date();
  const diffDays = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= PROPOSAL_EXPIRING_SOON_DAYS;
}

export function isExpired(expiryDate, status) {
  if (status === 'EXPIRED') return true;
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
}

export function needsFollowUp(proposal) {
  if (proposal.status !== 'VIEWED') return false;
  const anchor = proposal.viewedAt || proposal.sentAt || proposal.createdAt;
  if (!anchor) return false;
  const days = (Date.now() - new Date(anchor).getTime()) / (1000 * 60 * 60 * 24);
  return days >= PROPOSAL_FOLLOW_UP_DAYS;
}

export function mapProposalRow(proposal) {
  const status = proposal.status || 'DRAFT';
  return {
    id: proposal.id,
    proposalNo: proposal.proposalNumber,
    title: proposal.title,
    client: proposal.clientName,
    clientEmail: proposal.clientEmail || '',
    value: Number(proposal.value) || 0,
    status,
    version: proposal.version || 1,
    serviceType: proposal.serviceType || '—',
    frequency: proposal.frequency || '—',
    siteSize: proposal.siteSize || '—',
    staffRequired: proposal.staffRequired ?? '—',
    scopeOfWork: proposal.scopeOfWork || '—',
    terms: proposal.terms || '—',
    sentAt: proposal.sentAt,
    viewedAt: proposal.viewedAt,
    signedAt: proposal.signedAt,
    signedName: proposal.signedName,
    rejectReason: proposal.rejectReason,
    expiresAt: proposal.expiresAt,
    createdAt: proposal.createdAt,
    sentDateLabel: status === 'DRAFT' ? 'Not sent' : formatProposalDate(proposal.sentAt || proposal.createdAt),
    expiryLabel: formatProposalDate(proposal.expiresAt),
    raw: proposal,
  };
}

export function getStatusMeta(status) {
  const map = {
    SIGNED: { label: 'Signed', className: 'prop-status--signed' },
    VIEWED: { label: 'Viewed', className: 'prop-status--viewed' },
    SENT: { label: 'Sent', className: 'prop-status--sent' },
    REVISION: { label: 'Revision', className: 'prop-status--revision' },
    DECLINED: { label: 'Declined', className: 'prop-status--expired' },
    EXPIRED: { label: 'Expired', className: 'prop-status--expired' },
    DRAFT: { label: 'Draft', className: 'prop-status--draft' },
  };
  return map[status] || { label: status, className: 'prop-status--draft' };
}

export function buildTimeline(proposal) {
  const status = proposal.status || 'DRAFT';

  if (status === 'EXPIRED') {
    return [
      {
        key: 'DRAFT',
        label: 'Draft',
        date: proposal.createdAt,
        done: true,
        active: false,
      },
      {
        key: 'SENT',
        label: 'Sent',
        date: proposal.sentAt,
        done: Boolean(proposal.sentAt),
        active: false,
      },
      {
        key: 'EXPIRED',
        label: 'Expired',
        date: proposal.expiresAt,
        done: true,
        active: true,
      },
    ];
  }

  const steps = [
    { key: 'DRAFT', label: 'Draft', date: proposal.createdAt },
    { key: 'SENT', label: 'Sent', date: proposal.sentAt },
    { key: 'VIEWED', label: 'Viewed', date: proposal.viewedAt },
    { key: 'SIGNED', label: 'Signed', date: proposal.signedAt },
  ];

  const progressOrder = ['DRAFT', 'SENT', 'VIEWED', 'SIGNED'];
  const statusProgress = {
    DRAFT: 0,
    SENT: 1,
    VIEWED: 2,
    REVISION: 2,
    DECLINED: 1,
    SIGNED: 3,
  };
  const currentProgress = statusProgress[status] ?? 0;

  return steps.map((step, idx) => {
    const stepProgress = progressOrder.indexOf(step.key);
    const hasDate = Boolean(step.date);
    const done = stepProgress < currentProgress || (stepProgress === currentProgress && hasDate);
    return {
      ...step,
      done: done || (status === 'SIGNED' && step.key === 'SIGNED' && hasDate),
      active: status === step.key || (status === 'REVISION' && step.key === 'VIEWED'),
    };
  });
}
